<script setup>
import FormErrors from "../Errors/FormErrors.vue";
import {onMounted, reactive, ref} from "vue";
import {usePortalContentAndDesignStore} from "../../../../../stores/partners/portalContentAndDesignStore";
import EditableBtn from "../../../portal-editable-fields/EditableBtn.vue";
import Alert from "../../../../../elements/Alert.vue";
import EditableTextField from "../../../portal-editable-fields/EditableTextField.vue";
import {camelCase} from "../../../../../helpers/common";

const portalContentAndDesignStore = usePortalContentAndDesignStore();

const props = defineProps({
    partnerEmail: {
        type: String,
        default: null,
    },
    token: {
        type: String,
        default: null,
    },
    submitRoute: {
        type: String,
        default: 'partners_int.password.reset',
    },
});

onMounted(() => {
    if (props.partnerEmail) {
        formData.email = props.partnerEmail;
    }
    if (props.token) {
        formData.token = props.token;
    }
});

const formDataErrors = reactive({
            responseError: null,
            validationErrors: null,
        });

const formDataEmailSendSuccessfully = ref(false);

const formData = reactive({
    email: '',
    new_password:'',
    token: '',
});

const sendingFormData = ref(false);

const requestPasswordReset = async () => {
    formDataErrors.responseError = null;
    formDataErrors.validationErrors = null;
    sendingFormData.value = true;
    formDataEmailSendSuccessfully.value = false;

    axios.post(route(props.submitRoute), formData)
        .then((response) => {
            if (+response.data.status === 1) {
                formDataEmailSendSuccessfully.value = true;
                window.location = response.data.redirect_url;
            } else {
                formDataErrors.responseError = response.data?.error;
                if (response.data.redirect_url){
                    window.location = response.data.redirect_url;
                }
            }
        }).catch(error => {
            if (error.response.status === 422) {
                formDataErrors.validationErrors = error.response?.data?.errors;
            } else {
                console.log('Login error:', error.response);
                formDataErrors.responseError = error.response?.data?.error || "Something went wrong. See console for details.";
            }
        }).finally(() => {
            sendingFormData.value = false;
    })


    try {
    } catch (error) {
        if (error.response.status === 422) {
            formDataErrors.validationErrors = error.response.data.errors;
        } else {
            formDataErrors.responseError = error.response.data.message;
        }
    }
};

</script>

<template>
    <div :class="portalContentAndDesignStore.getPageContainerClasses">
        <form id="partners-forgotten-password-form">
            <div class="mb-6 space-y-4">

                <form-errors :store-form-errors="formDataErrors"/>
                <alert v-if="formDataEmailSendSuccessfully"
                       type="success"
                >
                    {{ portalContentAndDesignStore.translationSettings.portal_translation_pass_reset_password_successfully_changed }}
                </alert>

                <div>
                    <editable-text-field
                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_pass_reset_email_address"
                        for-attribute="email"
                        class="block text-sm opacity-80 mb-1.5"
                        :class="portalContentAndDesignStore.getPageContentTextColorClass"
                        text-type="label"
                    />
                    <input
                        type="email"
                        name="email"
                        id="email"
                        :class="portalContentAndDesignStore.getInputStyle"
                        v-model="formData.email"
                    >
                </div>

                <div>
                    <editable-text-field
                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_pass_reset_password"
                        :class="['block text-sm opacity-80 mb-1.5', portalContentAndDesignStore.getPageContentTextColorClass]"
                        for-attribute="password"
                        text-type="label"
                    />

                    <password
                        v-slot="{ childScope }"
                        :min="portalContentAndDesignStore.resetPasswordPageSettings.password_validation_rules.min"
                        :regex="portalContentAndDesignStore.resetPasswordPageSettings.password_validation_rules.regex"
                        :errors="false"
                        v-model="formData.new_password"
                        v-cloak
                    >
                        <div>
                            <input
                                v-model="childScope.password"
                                type="password"
                                name="password"
                                field-id="password"
                                :class="portalContentAndDesignStore.getInputStyle"
                            />

                            <div class="grid grid-cols-2 gap-2 mt-5">
                                <template v-for="(text, regex) in portalContentAndDesignStore.resetPasswordPageSettings.password_validation_rules.regex" :key="regex">
                                    <div class="flex items-center">
                                        <div
                                            class="h-2 w-2 rounded-full mr-2"
                                            :class="[childScope.rules[camelCase(text)] ? 'bg-black' : 'bg-gray-400']"
                                        ></div>
                                        <editable-text-field
                                            v-if="regex === '[a-z]'"
                                            v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_lowercase_character"
                                            :class="['text-xs tracking-wide', portalContentAndDesignStore.getPageContentTextColorClass]"
                                            text-type="span"
                                        />
                                        <editable-text-field
                                            v-else-if="regex === '[0-9]'"
                                            v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_one_number"
                                            :class="['text-xs tracking-wide', portalContentAndDesignStore.getPageContentTextColorClass]"
                                            text-type="span"
                                        />
                                        <editable-text-field
                                            v-else-if="regex === '[A-Z]'"
                                            v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_one_uppercase_character"
                                            :class="['text-xs tracking-wide', portalContentAndDesignStore.getPageContentTextColorClass]"
                                            text-type="span"
                                        />
                                    </div>
                                </template>

                                <div class="flex items-center">
                                    <div
                                        class="h-2 w-2 rounded-full mr-2"
                                        :class="[childScope.rules.passwordLength ? 'bg-black' : 'bg-gray-400']"
                                    ></div>
                                    <editable-text-field
                                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_characters_minimum"
                                        :class="['text-xs tracking-wide', portalContentAndDesignStore.getPageContentTextColorClass]"
                                        text-type="span"
                                    />
                                </div>
                            </div>
                        </div>
                    </password>
                </div>
            </div>
            <editable-btn
                :prevent-double-click="5000"
                :disabled="sendingFormData"
                :loading="sendingFormData"
                size="huge"
                class="w-full"
                :type="portalContentAndDesignStore.getPageContentButtonColorClass"
                :corners="portalContentAndDesignStore.getPublicPageButtonCorners"
                v-model="portalContentAndDesignStore.translationSettings.portal_translation_pass_reset_reset_password"
                @click="requestPasswordReset"
            />
        </form>
    </div>
</template>
